import { useCallback, useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import CrmShell from "@/components/crm/CrmShell";
import { Panel, inputCls } from "@/components/crm/ui";

const NEW_USER = { name: "", email: "", phone: "", password: "", role: "employee" };

export default function Team() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState(NEW_USER);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const [u, s] = await Promise.all([api.get("/users"), api.get("/dashboard/stats")]);
      setUsers(u.data);
      setStats(s.data);
    } catch (e) {
      toast.error(errText(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("Advisor added");
      setForm(NEW_USER);
      setCreating(false);
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const toggleStatus = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { status: u.status === "active" ? "inactive" : "active" });
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const perf = (id) => stats?.team?.find((t) => t.id === id);

  return (
    <CrmShell
      title="Team"
      subtitle="Advisors, access and performance"
      actions={
        <button
          onClick={() => setCreating((v) => !v)}
          data-testid="toggle-create-user"
          className="flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-xs font-bold text-white"
        >
          {creating ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          {creating ? "Cancel" : "Add advisor"}
        </button>
      }
    >
      {creating && (
        <Panel className="mb-5" data-testid="create-user-panel">
          <form onSubmit={create} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" className={inputCls} />
            <input required type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" className={inputCls} />
            <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
            <input required type="password" placeholder="Temporary password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" className={inputCls} />
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role-select" className={inputCls}>
              <option value="employee" className="bg-white">Advisor (employee)</option>
              <option value="admin" className="bg-white">Owner (admin)</option>
            </select>
            <button type="submit" data-testid="create-user-submit" className="rounded-full bg-brand py-2.5 text-xs font-bold text-white">
              Create account
            </button>
          </form>
        </Panel>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="team-list">
        {users.map((u) => {
          const p = perf(u.id);
          return (
            <Panel key={u.id} data-testid={`team-card-${u.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm font-semibold text-ink">{u.name}</div>
                  <div className="mt-0.5 text-[11px] text-body">{u.email}</div>
                  {u.phone && <div className="text-[11px] text-muted2">{u.phone}</div>}
                </div>
                <span className="rounded-full border border-brand/30 bg-mint px-2 py-0.5 text-[10px] uppercase text-brand">
                  {u.role}
                </span>
              </div>
              {p && (
                <div className="mt-4 grid grid-cols-3 gap-2 border-t border-line pt-3 text-center">
                  {[
                    ["Leads", p.leads],
                    ["Closed", p.closed],
                    ["Conv %", p.conversion],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <div className="font-display text-base font-semibold text-ink">{v}</div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-muted2">{k}</div>
                    </div>
                  ))}
                </div>
              )}
              <button
                onClick={() => toggleStatus(u)}
                data-testid={`toggle-status-${u.id}`}
                className={`mt-4 w-full rounded-full border py-2 text-[11px] font-semibold ${
                  u.status === "active"
                    ? "border-line text-body hover:border-red-200 hover:text-red-600"
                    : "border-emerald-200 text-emerald-600"
                }`}
              >
                {u.status === "active" ? "Deactivate" : "Reactivate"}
              </button>
            </Panel>
          );
        })}
      </div>
    </CrmShell>
  );
}
